import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Metric,
  Notice,
  Status,
  TICKET_PRODUCT_STATES,
  formatMoneyOrFree,
  presentation,
} from '@gatecheckpro/design-system';
import type { OrganizerEvent, TicketProduct, Venue } from '@gatecheckpro/contracts';
import type { EventDemand, EventSales } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

/**
 * Ticket products and capacity.
 *
 * Two things are deliberately on one page. Organizers do not think about a
 * "ticket product" and its "inventory allocation" as separate objects — they
 * think "1,000 general admission at ₦15,000". Keeping them together is what lets
 * the page answer the question they actually have, which is how many they can
 * sell in total and whether that adds up.
 *
 * What it must not do is imply anything is on sale. Checkout is Slice 3.
 */
export function EventTicketsPage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const command = useCommand<OrganizerEvent>();
  const base = `/api/v1/organizer/organizations/${organizationId}`;
  const eventBase = `${base}/events/${event.eventId}`;

  const { resource: venue } = useResource<Venue>(
    event.venue === null ? null : `${base}/venues/${event.venue.venueId}`,
  );

  // Demand, read separately from the event so it can refresh on its own without
  // re-reading every gate and session.
  const { resource: demand } = useResource<EventDemand>(`${eventBase}/demand`);

  // Sales, read separately from demand — and deliberately never merged into it.
  // "Reserved" and "sold" are different facts with different consequences, and a
  // dashboard that adds them together is a dashboard that has already lied.
  const { resource: sales } = useResource<EventSales>(`${eventBase}/sales`);

  const [showProductForm, setShowProductForm] = useState(false);
  const [showPoolForm, setShowPoolForm] = useState(false);
  const [editing, setEditing] = useState<TicketProduct | null>(null);

  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMajor, setPriceMajor] = useState('');
  const [currency, setCurrency] = useState('NGN');
  const [zoneId, setZoneId] = useState('');
  const [securityMode, setSecurityMode] = useState('STANDARD_PASS');

  const [poolCode, setPoolCode] = useState('');
  const [poolName, setPoolName] = useState('');
  const [poolCapacity, setPoolCapacity] = useState('');

  const zones = venue.status === 'loaded' ? venue.data.zones : [];
  const totalSaleable = event.ticketProducts
    .filter((product) => product.status === 'PUBLISHED')
    .reduce((sum, product) => sum + product.capacity.allocated, 0);
  const totalCapacity = event.inventoryPools.reduce((sum, pool) => sum + pool.capacity, 0);

  function resetProductForm(): void {
    setCode('');
    setDisplayName('');
    setDescription('');
    setPriceMajor('');
    setZoneId('');
    setSecurityMode('STANDARD_PASS');
    setEditing(null);
    setShowProductForm(false);
  }

  function beginEdit(product: TicketProduct): void {
    setEditing(product);
    setDisplayName(product.currentVersion.displayName);
    setDescription(product.currentVersion.description ?? '');
    setPriceMajor(String(product.currentVersion.price.minor / 100));
    setCurrency(product.currentVersion.price.currency);
    setZoneId(product.currentVersion.accessZoneId ?? '');
    setSecurityMode(product.currentVersion.securityMode);
    setShowProductForm(true);
  }

  async function saveProduct(formEvent: React.FormEvent): Promise<void> {
    formEvent.preventDefault();
    const terms = {
      displayName,
      description: description === '' ? null : description,
      // Entered in major units and converted once, here. The wire format is
      // always integer minor units.
      price: { minor: Math.round(Number(priceMajor) * 100), currency },
      accessScope: 'ALL_SESSIONS' as const,
      sessionIds: [],
      accessZoneId: zoneId === '' ? null : zoneId,
      securityMode,
    };

    const result =
      editing === null
        ? await command.run(`${eventBase}/ticket-products`, {
            method: 'POST',
            body: { code, ...terms },
          })
        : await command.run(`${eventBase}/ticket-products/${editing.ticketProductId}`, {
            method: 'PUT',
            body: terms,
          });

    if (result !== null) {
      resetProductForm();
      reload();
    }
  }

  async function createPool(formEvent: React.FormEvent): Promise<void> {
    formEvent.preventDefault();
    const result = await command.run(`${eventBase}/inventory-pools`, {
      method: 'POST',
      body: {
        code: poolCode,
        displayName: poolName,
        capacity: Number(poolCapacity),
      },
    });
    if (result !== null) {
      setPoolCode('');
      setPoolName('');
      setPoolCapacity('');
      setShowPoolForm(false);
      reload();
    }
  }

  async function allocate(
    ticketProductId: string,
    inventoryPoolId: string,
    quantity: number,
  ): Promise<void> {
    const result = await command.run(`${eventBase}/allocations`, {
      method: 'PUT',
      body: { inventoryPoolId, kind: 'SALEABLE', ticketProductId, quantity },
    });
    if (result !== null) reload();
  }

  async function setStatus(ticketProductId: string, status: string): Promise<void> {
    const result = await command.run(`${eventBase}/ticket-products/${ticketProductId}/status`, {
      method: 'PUT',
      body: { status },
    });
    if (result !== null) reload();
  }

  return (
    <div className="gc-stack gc-stack--loose">
      {/*
        What an attendee can actually do, stated where an organizer decides
        prices. Checkout and payment are both real from Slice 4; admission at a
        gate is not, and conflating the two would let an organizer plan a door
        process the product cannot yet run.
      */}
      <Notice tone="info" title="These tickets can be bought">
        Attendees can reserve and pay for these now, and a confirmed payment issues a ticket with a
        pass they can show. Scanning that pass at a gate arrives in the next release.
      </Notice>

      <FailureNotice error={command.error} />

      <div className="gc-grid">
        <Metric
          label="Total configured capacity"
          value={totalCapacity.toLocaleString()}
          hint={`across ${String(event.inventoryPools.length)} pool${event.inventoryPools.length === 1 ? '' : 's'}`}
        />
        <Metric
          label="Can ultimately be sold"
          value={totalSaleable.toLocaleString()}
          hint="published products only"
        />
        <Metric
          label="Customers holding now"
          value={demand.status === 'loaded' ? demand.data.activeHolds.toLocaleString() : '—'}
          hint="tickets reserved mid-checkout"
          {...(demand.status === 'loaded' && demand.data.activeHolds > 0
            ? { tone: 'caution' as const }
            : {})}
        />
        <Metric
          label="Available right now"
          value={demand.status === 'loaded' ? demand.data.availableNow.toLocaleString() : '—'}
          hint="after live reservations"
        />
        <Metric
          label="Sold"
          value={sales.status === 'loaded' ? sales.data.ticketsSold.toLocaleString() : '—'}
          hint="payment confirmed with the provider"
          {...(sales.status === 'loaded' && sales.data.ticketsSold > 0
            ? { tone: 'positive' as const }
            : {})}
        />
      </div>

      {sales.status === 'loaded' && sales.data.ticketsSold > 0 && (
        <Card title="Confirmed sales" eyebrow="Verified with the payment provider">
          <div className="gc-stack gc-stack--tight">
            <div className="gc-row gc-row--between">
              <span>Provider-confirmed gross ticket sales</span>
              <span className="gc-metric__value">
                {sales.data.confirmedGross === null
                  ? '—'
                  : formatMoneyOrFree(sales.data.confirmedGross)}
              </span>
            </div>
            <div className="gc-row gc-row--between">
              <span>Tickets issued</span>
              <span className="gc-metric__value">{sales.data.ticketsIssued.toLocaleString()}</span>
            </div>
            {sales.data.ordersAwaitingIssuance > 0 && (
              <p className="gc-muted">
                {sales.data.ordersAwaitingIssuance} paid order
                {sales.data.ordersAwaitingIssuance === 1 ? '' : 's'} still being issued. Tickets
                appear for those customers within moments.
              </p>
            )}
            {/*
              Gross, and said so. There is no ledger, no settlement engine and no
              payout schedule in this release, so the words balance, payable,
              settlement due, available payout and net revenue must not appear —
              each would state a financial position GateCheckPro cannot compute.
            */}
            <p className="gc-muted">{sales.data.note}</p>
          </div>
        </Card>
      )}

      {demand.status === 'loaded' && demand.data.paymentPendingOrders > 0 && (
        <Card title="Awaiting payment" eyebrow="Reservations, not sales">
          <div className="gc-stack gc-stack--tight">
            <div className="gc-row gc-row--between">
              <span>
                {demand.data.paymentPendingOrders} order
                {demand.data.paymentPendingOrders === 1 ? '' : 's'} awaiting payment
              </span>
              {demand.data.paymentPendingAmount !== null && (
                <span className="gc-metric__value">
                  {formatMoneyOrFree(demand.data.paymentPendingAmount)}
                </span>
              )}
            </div>
            {/*
              Never totalled as revenue. Nothing here has been paid, and a figure
              labelled sales would be the first lie in the financial chain.
            */}
            <p className="gc-muted">{demand.data.note}</p>
          </div>
        </Card>
      )}

      {/* --- Capacity pools --- */}
      <Card
        title="Capacity"
        eyebrow="What physically exists"
        action={
          <Button onClick={() => setShowPoolForm((current) => !current)}>
            {showPoolForm ? 'Cancel' : 'Add a pool'}
          </Button>
        }
      >
        <div className="gc-stack">
          <p className="gc-muted">
            A pool is a real quantity — an arena floor, a balcony. Several ticket products can draw
            from one pool, which is what stops three products each believing they own the whole
            room.
          </p>

          {event.inventoryPools.length === 0 ? (
            <EmptyState title="No capacity configured">
              Add a pool before allocating tickets to it.
            </EmptyState>
          ) : (
            <div className="gc-table-scroll">
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">Pool</th>
                    <th scope="col">Capacity</th>
                    <th scope="col">Allocated to tickets</th>
                    <th scope="col">Held back</th>
                    <th scope="col">Blocked</th>
                    <th scope="col">Unallocated</th>
                  </tr>
                </thead>
                <tbody>
                  {event.inventoryPools.map((pool) => (
                    <tr key={pool.inventoryPoolId}>
                      <td>
                        <div className="gc-stack gc-stack--tight">
                          <strong>{pool.displayName}</strong>
                          <span className="gc-muted gc-mono">{pool.code}</span>
                        </div>
                      </td>
                      <td>{pool.capacity.toLocaleString()}</td>
                      <td>{pool.saleableAllocated.toLocaleString()}</td>
                      <td>{pool.protectedAllocations.toLocaleString()}</td>
                      <td>{pool.blockedCapacity.toLocaleString()}</td>
                      <td>{pool.unallocatedAvailable.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showPoolForm && (
            <form className="gc-stack" onSubmit={(formEvent) => void createPool(formEvent)}>
              <div className="gc-grid">
                <Field
                  label="Code"
                  required
                  placeholder="MAIN_FLOOR"
                  hint="Stable. Referenced later by reports and gate packages."
                  value={poolCode}
                  onChange={(input) => setPoolCode(input.target.value.toUpperCase())}
                />
                <Field
                  label="Name"
                  required
                  placeholder="Main floor"
                  value={poolName}
                  onChange={(input) => setPoolName(input.target.value)}
                />
                <Field
                  label="Capacity"
                  type="number"
                  min={0}
                  required
                  value={poolCapacity}
                  onChange={(input) => setPoolCapacity(input.target.value)}
                />
              </div>
              <Button type="submit" variant="primary" busy={command.busy}>
                Add pool
              </Button>
            </form>
          )}
        </div>
      </Card>

      {/* --- Ticket products --- */}
      <Card
        title="Ticket products"
        eyebrow="What you sell"
        action={
          <Button
            variant="primary"
            onClick={() => {
              resetProductForm();
              setShowProductForm(true);
            }}
          >
            Add a ticket
          </Button>
        }
      >
        <div className="gc-stack">
          {event.ticketProducts.length === 0 ? (
            <EmptyState title="No ticket products yet">
              A ticket product is what an attendee buys: a name, a price, and the zone it lets them
              into.
            </EmptyState>
          ) : (
            event.ticketProducts.map((product) => {
              const state = presentation(TICKET_PRODUCT_STATES, product.status);
              const pool = event.inventoryPools.find(
                (candidate) => candidate.inventoryPoolId === product.capacity.inventoryPoolId,
              );
              return (
                <div key={product.ticketProductId} className="gc-card">
                  <div className="gc-stack">
                    <div className="gc-row gc-row--between">
                      <div className="gc-stack gc-stack--tight">
                        <div className="gc-row gc-row--baseline">
                          <h3>{product.currentVersion.displayName}</h3>
                          <Status
                            tone={state.tone}
                            glyph={state.glyph}
                            label={state.label}
                            title={state.meaning}
                          />
                        </div>
                        <span className="gc-muted gc-mono">
                          {product.code} · version {product.currentVersion.version}
                        </span>
                      </div>
                      <span className="gc-ticket-card__price">
                        {formatMoneyOrFree(product.currentVersion.price)}
                      </span>
                    </div>

                    {product.currentVersion.description !== null && (
                      <p className="gc-muted">{product.currentVersion.description}</p>
                    )}

                    <div className="gc-row">
                      {product.currentVersion.accessZoneDisplayName === null ? (
                        <Status
                          tone="caution"
                          label="No zone"
                          title="No gate could admit this ticket."
                        />
                      ) : (
                        <Status
                          tone="info"
                          glyph="◈"
                          label={`Admits to ${product.currentVersion.accessZoneDisplayName}`}
                        />
                      )}
                      <Status
                        tone="neutral"
                        glyph="◇"
                        label={
                          product.currentVersion.securityMode === 'SECURE_PASS'
                            ? 'Secure pass'
                            : 'Standard pass'
                        }
                        title={
                          product.currentVersion.securityMode === 'SECURE_PASS'
                            ? 'Requires the GateCheck app on the attendee\u2019s phone, and a deployment where Secure Pass is released.'
                            : 'A signed code the attendee can show from any browser.'
                        }
                      />
                    </div>

                    <div className="gc-row gc-row--between">
                      <div className="gc-row">
                        <label
                          className="gc-field__label"
                          htmlFor={`alloc-${product.ticketProductId}`}
                        >
                          Allocated
                        </label>
                        <input
                          id={`alloc-${product.ticketProductId}`}
                          className="gc-input"
                          style={{ width: '8rem' }}
                          type="number"
                          min={0}
                          defaultValue={product.capacity.allocated}
                          onBlur={(input) => {
                            const quantity = Number(input.target.value);
                            const target =
                              product.capacity.inventoryPoolId ??
                              event.inventoryPools[0]?.inventoryPoolId;
                            if (target !== undefined && quantity !== product.capacity.allocated) {
                              void allocate(product.ticketProductId, target, quantity);
                            }
                          }}
                          disabled={event.inventoryPools.length === 0}
                        />
                        <span className="gc-muted">
                          {pool === undefined
                            ? event.inventoryPools.length === 0
                              ? 'Add a capacity pool first'
                              : `from ${event.inventoryPools[0]?.displayName ?? ''}`
                            : `from ${pool.displayName}`}
                        </span>
                      </div>

                      <div className="gc-row">
                        <Button onClick={() => beginEdit(product)}>Revise terms</Button>
                        {product.status === 'DRAFT' ? (
                          <Button
                            variant="primary"
                            onClick={() => void setStatus(product.ticketProductId, 'PUBLISHED')}
                          >
                            Show on the event page
                          </Button>
                        ) : product.status === 'PUBLISHED' ? (
                          <Button onClick={() => void setStatus(product.ticketProductId, 'DRAFT')}>
                            Hide from the page
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    <Link
                      to={`../versions/${product.ticketProductId}`}
                      className="gc-muted"
                      style={{ fontSize: 'var(--gc-text-xs)' }}
                    >
                      See every version of these terms →
                    </Link>
                  </div>
                </div>
              );
            })
          )}

          {showProductForm && (
            <Card
              title={editing === null ? 'New ticket product' : `Revise ${editing.code}`}
              eyebrow={
                editing === null
                  ? 'What an attendee buys'
                  : 'Creates a new version — the old terms are kept'
              }
            >
              <form className="gc-stack" onSubmit={(formEvent) => void saveProduct(formEvent)}>
                {editing !== null && (
                  <Notice tone="info" title="Revising creates a version">
                    The current terms are kept and superseded, not overwritten. Anyone who has
                    already agreed to them keeps what they agreed to.
                  </Notice>
                )}

                {editing === null && (
                  <Field
                    label="Code"
                    required
                    placeholder="GA"
                    hint="Stable and permanent. Reports and gate packages reference it."
                    value={code}
                    onChange={(input) => setCode(input.target.value.toUpperCase())}
                  />
                )}
                <Field
                  label="Name shown to attendees"
                  required
                  placeholder="General Admission"
                  value={displayName}
                  onChange={(input) => setDisplayName(input.target.value)}
                />
                <Field
                  label="Description"
                  placeholder="Standing, main floor."
                  value={description}
                  onChange={(input) => setDescription(input.target.value)}
                />
                <div className="gc-grid">
                  <Field
                    label="Price"
                    type="number"
                    min={0}
                    step="0.01"
                    required
                    hint="0 for a free ticket."
                    value={priceMajor}
                    onChange={(input) => setPriceMajor(input.target.value)}
                  />
                  <div className="gc-field">
                    <label className="gc-field__label" htmlFor="currency">
                      Currency
                    </label>
                    <select
                      id="currency"
                      className="gc-select"
                      value={currency}
                      onChange={(input) => setCurrency(input.target.value)}
                    >
                      <option value="NGN">NGN — Nigerian naira</option>
                      <option value="GHS">GHS — Ghanaian cedi</option>
                      <option value="KES">KES — Kenyan shilling</option>
                      <option value="ZAR">ZAR — South African rand</option>
                      <option value="USD">USD — US dollar</option>
                      <option value="GBP">GBP — Pound sterling</option>
                    </select>
                  </div>
                </div>

                <div className="gc-field">
                  <label className="gc-field__label" htmlFor="zone">
                    Which zone does this admit to?
                  </label>
                  <select
                    id="zone"
                    className="gc-select"
                    value={zoneId}
                    onChange={(input) => setZoneId(input.target.value)}
                  >
                    <option value="">Choose a zone</option>
                    {zones.map((zone) => (
                      <option key={zone.accessZoneId} value={zone.accessZoneId}>
                        {zone.displayName}
                      </option>
                    ))}
                  </select>
                  <span className="gc-field__hint">
                    Needed before this ticket can go on the page — a ticket with no zone is one no
                    gate could admit.
                  </span>
                </div>

                <div className="gc-field">
                  <label className="gc-field__label" htmlFor="security">
                    Credential
                  </label>
                  <select
                    id="security"
                    className="gc-select"
                    value={securityMode}
                    onChange={(input) => setSecurityMode(input.target.value)}
                  >
                    <option value="STANDARD_PASS">
                      Standard pass — a signed code, shown from any browser
                    </option>
                    <option value="SECURE_PASS">
                      Secure pass — device-bound, refreshes constantly
                    </option>
                  </select>
                  <span className="gc-field__hint">
                    A commercial term: it changes what an attendee needs to bring, so it cannot be
                    changed under them once they have bought. Secure pass needs the GateCheck app;
                    where a deployment has not released it, checkout refuses the order before any
                    money moves rather than selling an assurance it cannot deliver.
                  </span>
                </div>

                <div className="gc-row">
                  <Button
                    type="submit"
                    variant="primary"
                    busy={command.busy}
                    disabled={displayName.length === 0 || priceMajor === ''}
                  >
                    {editing === null ? 'Add ticket product' : 'Save as a new version'}
                  </Button>
                  <Button onClick={resetProductForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}
        </div>
      </Card>
    </div>
  );
}
